import { gql, useLazyQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { IAbandonedBusinessTokenData } from '../../interfaces/abandoned-business/IAbandonedBusinessTokenData';

type TokenState =
  | { status: 'loading' }
  | { status: 'success'; data: IAbandonedBusinessTokenData }
  | { status: 'already_confirmed'; data: IAbandonedBusinessTokenData }
  | { status: 'expired'; data: IAbandonedBusinessTokenData }
  | { status: 'error'; message: string };

const GET_FRIDGE_VISIT_RECOVERY_BY_TOKEN = gql`
  query GetFridgeVisitRecoveryByTokenQuery($token: String!) {
    getFridgeVisitRecoveryByToken(token: $token) {
      message
      status
      statusCode
      error
      data {
        isValid
        isExpired
        isAlreadyConfirmed
        expiresAt
        userId
        fridgeId
        fridgeName
        openedAt
        availableDishes {
          id
          name
          image
          category
        }
        userName
        firstName
        userEmail
        userPreferredLanguage
      }
    }
  }
`;

const useAbandonedBusinessToken = (token: string | undefined): TokenState => {
  const [state, setState] = useState<TokenState>({ status: 'loading' });
  const [getData, { data, loading, error, called }] = useLazyQuery(
    GET_FRIDGE_VISIT_RECOVERY_BY_TOKEN,
    { fetchPolicy: 'no-cache' },
  );

  useEffect(() => {
    // `useParams()` can return `undefined` briefly during navigation.
    // Avoid flashing an error screen before we have a real token.
    if (token === undefined) return;

    const trimmedToken = token.trim();
    if (!trimmedToken) {
      setState({ status: 'error', message: 'No token provided' });
      return;
    }

    setState({ status: 'loading' });
    getData({ variables: { token: trimmedToken } });
  }, [token, getData]);

  useEffect(() => {
    const trimmedToken = token?.trim();
    if (!trimmedToken) return;

    // `useLazyQuery` starts with `{ called: false, loading: false, data: undefined }`.
    // If we treat that as a completed request, we briefly show an error before the query starts.
    if (!called) return;

    if (loading) {
      setState({ status: 'loading' });
      return;
    }

    if (error) {
      setState({ status: 'error', message: 'Validation failed' });
      return;
    }

    const tokenData = data?.getFridgeVisitRecoveryByToken?.data as
      | IAbandonedBusinessTokenData
      | undefined;

    if (!tokenData?.isValid) {
      setState({ status: 'error', message: 'Invalid link' });
    } else if (tokenData.isExpired) {
      setState({ status: 'expired', data: tokenData });
    } else if (tokenData.isAlreadyConfirmed) {
      setState({ status: 'already_confirmed', data: tokenData });
    } else {
      setState({ status: 'success', data: tokenData });
    }
  }, [token, called, loading, error, data]);

  return state;
};

export default useAbandonedBusinessToken;
